import { Box, Button, Header, Link, SpaceBetween, Table } from '@cloudscape-design/components';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { listZipBlasts } from '../../common/api/zipBlasts';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { ZipBlastStatusBadge } from '../../components/ZipBlastStatusBadge';
import { ZipBlastMetricsPanel } from '../../components/dashboard/ZipBlastMetricsPanel';

type ZipBlastListItem = NonNullable<Awaited<ReturnType<typeof listZipBlasts>>>['items'][number];

function formatScope(item: ZipBlastListItem): string {
  if (item.area_label) return item.area_label;
  if (item.city || item.state) return [item.city, item.state].filter(Boolean).join(', ');
  return item.zip_code;
}

export default function ZipBlastsListPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['zip-blasts', 1, 50],
    queryFn: () => listZipBlasts(1, 50),
    refetchInterval: 5000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const inFlight = items.filter((i) =>
    ['pending', 'discovering', 'building', 'pushing_leads', 'scheduling'].includes(i.status),
  ).length;

  return (
    <DashboardViewLayout
      breadcrumbs={[{ text: 'Zip Blasts', href: '/dashboard/zip-blasts' }]}
      contentType="table"
    >
      <SpaceBetween size="l" direction="vertical">
        <ZipBlastMetricsPanel />
        <Table
          variant="full-page"
          stickyHeader
          loading={isLoading}
          loadingText="Loading zip blasts…"
          items={items}
          empty={
            <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
              {error ? (
                <SpaceBetween size="xxs">
                  <div>
                    <b>Failed to load zip blasts</b>
                    <Box color="inherit" variant="p">
                      {String(error)}
                    </Box>
                  </div>
                  <Button onClick={() => refetch()}>Retry</Button>
                </SpaceBetween>
              ) : (
                <SpaceBetween size="xxs">
                  <div>
                    <b>No zip blasts yet</b>
                    <Box color="inherit" variant="p">
                      Click "New zip blast" to start one.
                    </Box>
                  </div>
                </SpaceBetween>
              )}
            </Box>
          }
          header={
            <Header
              variant="awsui-h1-sticky"
              counter={`(${total})`}
              description={`${inFlight} in flight · auto-refresh every 5s · manually-triggered restaurant scorecard blasts`}
              actions={
                <SpaceBetween size="xs" direction="horizontal">
                  <Button onClick={() => refetch()} loading={isFetching} iconName="refresh" />
                  <Button
                    variant="primary"
                    onClick={() => navigate({ to: '/dashboard/zip-blasts/new' })}
                  >
                    New zip blast
                  </Button>
                </SpaceBetween>
              }
            >
              Zip Blasts
            </Header>
          }
          columnDefinitions={[
            {
              id: 'scope',
              header: 'City / area',
              cell: (i) => (
                <Link
                  href={`/dashboard/zip-blasts/${i.uuid}`}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate({ to: '/dashboard/zip-blasts/$uuid', params: { uuid: i.uuid } });
                  }}
                >
                  {formatScope(i)}
                </Link>
              ),
              minWidth: 160,
            },
            {
              id: 'zip',
              header: 'Zip',
              cell: (i) => i.zip_code,
              width: 110,
            },
            {
              id: 'children',
              header: 'Child ZIPs',
              cell: (i) =>
                i.child_count ? `${i.completed_child_count}/${i.child_count} complete` : '—',
              width: 160,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (i) => <ZipBlastStatusBadge status={i.status} />,
              width: 200,
            },
            {
              id: 'progress',
              header: 'Progress',
              cell: (i) =>
                `${i.places_resolved}/${i.listings_found} resolved · ` +
                `${i.leads_pushed} leads · ${i.emails_scheduled} scheduled`,
              minWidth: 280,
            },
            {
              id: 'created',
              header: 'Created',
              cell: (i) => new Date(i.created_at).toLocaleString(),
              width: 200,
            },
            {
              id: 'completed',
              header: 'Completed',
              cell: (i) => (i.completed_at ? new Date(i.completed_at).toLocaleString() : '—'),
              width: 200,
            },
          ]}
        />
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
